import { LoginParams, LoginResponse, Verify2FAParams, Verify2FAResponse } from "@/types/auth";
import { WEBMSG_ENDPOINT } from "@/constants/links";
import { extractUserId } from "@/utils/auth";
import { attachTraceId } from "@/utils/traceId";
import { base64ToArrayBuffer } from "@/utils/crypto";

import api from "./api";
import axios from "axios";

// Loosely-typed shape of the raw login_check.php payload (the fields the mapper reads).
interface RawAuthPayload {
  firstName?: string;
  lastName?: string;
  userId?: number | string | null;
  phase?: string;
  qrCodeUrl?: string;
  setupKey?: string;
  codetype?: number;
  permissions?: string[];
  router_info?: LoginResponse["router_info"];
  [key: string]: unknown;
}

/**
 * The router's stored spelling (`u_name`) from the JWT that login/2FA responses echo: login matches
 * case-insensitively, so the typed name can differ from every router-supplied one. Unverified, read only.
 */
function usernameFromJwt(jwt: unknown): string | undefined {
  if (typeof jwt !== "string") return undefined;
  try {
    const payload = base64ToArrayBuffer(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"));
    const name = JSON.parse(new TextDecoder().decode(payload))?.data?.username;
    return typeof name === "string" && name ? name : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Builds a LoginResponse — shared by loginApi's 2FA-needed and full-auth branches (they
 * differ only in `authenticated`). userId is null, never a fake-valid 0, when unresolved.
 */
function mapLoginResponse(data: RawAuthPayload, params: LoginParams, authenticated: number): LoginResponse {
  return {
    // No JWT yet on the 2FA-needed branch; the verify response's JWT corrects it.
    username: usernameFromJwt(data.jwt) ?? params.username,
    firstName: data.firstName || "",
    lastName: data.lastName || "",
    userId: extractUserId(data) ?? null,
    authenticated,
    routerId: data.router_info?.router_id || params.routerId,
    phase: data.phase,
    qrCodeUrl: data.qrCodeUrl,
    setupKey: data.setupKey,
    codetype: data.codetype,
    permissions: data.permissions,
    router_info: data.router_info,
  };
}

// Create a separate axios instance for login endpoints without baseURL
// This allows requests to go directly to /admin/api/... without the /router/ prefix
const loginApiInstance = axios.create({
  timeout: 5000,
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
  withCredentials: true,
});

attachTraceId(loginApiInstance);

/**
 * Function to handle the login API call using Check2FALogin.
 *
 * @param {LoginParams} loginParams - The login parameters (routerId, username, password, deviceId, trustDevice).
 * @returns {Promise<LoginResponse>} - The login response from the API.
 * @throws {Error} - Throws an error if the login fails.
 */
export async function loginApi(
  loginParams: LoginParams
): Promise<LoginResponse> {
  const data = {
    action: "login",
    username: loginParams.username,
    password: loginParams.password,
    deviceid: loginParams.deviceId,
    trustdevice: loginParams.trustDevice ? 1 : 0,
  };
  
  const response = await loginApiInstance.post("/admin/api/login_check.php", data);

  // Check if 2FA is needed before checking success
  // Backend may return success: false when 2FA is required
  if (response.data.phase === "2fa_needed" || response.data.phase === "reset_password_needed") {
    return mapLoginResponse(response.data, loginParams, 0); // not fully authenticated yet
  }

  if (!response.data.success) {
    throw new Error(response.data.message || response.data.error || "Login failed");
  }

  return mapLoginResponse(response.data, loginParams, response.data.success ? 1 : 0);
}

/**
 * Logs out the user via login_check.php, which clears cookies and unbinds CAC.
 */
export const revokeTokens = async () => {
  return loginApiInstance.post("/admin/api/login_check.php", { action: "logout" });
};

/**
 * Sends a request to validate a JWT.
 *
 * @param {string} username - The username of the user.
 * @param {number} routerId - The router ID associated with the user.
 * @returns {Promise<AxiosResponse>} The response from the API.
 */
export const validateJWT = async (username: string, routerId: number) => {
  const data = {
    username,
    router_id: routerId,
    cmd: "ValidateJWT",
  };

  return api.post(WEBMSG_ENDPOINT, data);
};

/**
 * Sends a request to refresh the JWT.
 *
 * @param {string} username - The username of the user.
 * @param {number} routerId - The router ID associated with the user.
 * @returns {Promise<AxiosResponse>} The response from the API.
 */
export const refreshJWT = async (username: string, routerId: number) => {
  const data = {
    username: username,
    router_id: routerId,
    cmd: "RefreshJWT",
  };
  return api.post(WEBMSG_ENDPOINT, data);
};

/**
 * Verifies the 2FA code provided by the user.
 *
 * @param {Verify2FAParams} params - The 2FA verification parameters.
 * @returns {Promise<Verify2FAResponse>} The response from the API.
 * @throws {Error} Throws an error if verification fails.
 */
export async function verify2FAApi(
  params: Verify2FAParams
): Promise<Verify2FAResponse> {
  const data = {
    action: "verify_2fa",
    code: params.code,
    deviceid: params.deviceId,
    trustdevice: params.trustDevice ? 1 : 0,
    initialemail: params.initialEmail ? 1 : 0,
    useemailauth: params.useEmailAuth ? 1 : 0,
    ...(params.password && { password: params.password }),
  };

  const response = await loginApiInstance.post("/admin/api/login_check.php", data);

  if (!response.data.success) {
    throw new Error(response.data.message || response.data.error || "2FA verification failed");
  }

  return {
    success: response.data.success,
    message: response.data.message,
    userId: extractUserId(response.data),
    username: usernameFromJwt(response.data.jwt) ?? params.username,
    firstName: response.data.firstName,
    lastName: response.data.lastName,
    routerId: response.data.router_info?.router_id,
    permissions: response.data.permissions,
    router_info: response.data.router_info,
  } as Verify2FAResponse;
}

/**
 * Sends a 2FA code via email for verification (login or setup).
 *
 * @returns {Promise<{success: boolean, message: string}>} The response from the API.
 * @throws {Error} Throws an error if the request fails.
 */
export async function send2FACodeEmailApi(password?: string): Promise<{ success: boolean; message: string }> {
  const data = {
    action: "send_initial_2fa_email",
    ...(password && { password }),
  };

  const response = await loginApiInstance.post("/admin/api/login_check.php", data);

  if (!response.data.success) {
    throw new Error(response.data.message || response.data.error || "Failed to send 2FA email");
  }

  return {
    success: response.data.success,
    message: response.data.message || "2FA code sent to your email",
  };
}

/**
 * Sends a recovery 2FA code via email (lost authenticator).
 *
 * @returns {Promise<{success: boolean, message: string}>} The response from the API.
 * @throws {Error} Throws an error if the request fails.
 */
export async function send2FARecoveryEmailApi(password?: string): Promise<{ success: boolean; message: string }> {
  const data = {
    action: "send_reset_2fa_email",
    ...(password && { password }),
  };

  const response = await loginApiInstance.post("/admin/api/login_check.php", data);

  if (!response.data.success) {
    throw new Error(response.data.message || response.data.error || "Failed to send recovery email");
  }

  return {
    success: response.data.success,
    message: response.data.message || "Recovery code sent to your email",
  };
}

/**
 * Verifies a 2FA reset code (for recovery when user lost authenticator).
 *
 * @param {Object} params - Parameters for reset code verification.
 * @param {string} params.code - The reset code from email.
 * @param {string} params.deviceId - The device ID.
 * @param {boolean} params.trustDevice - Whether to trust this device.
 * @returns {Promise<Verify2FAResponse>} The response from the API.
 * @throws {Error} Throws an error if verification fails.
 */
export async function verify2FAResetCodeApi(params: {
  code: string;
  deviceId: string;
  trustDevice: boolean;
  password?: string;
}): Promise<Verify2FAResponse> {
  const data = {
    action: "verify_2fa_reset_code",
    code: params.code,
    deviceid: params.deviceId,
    trustdevice: params.trustDevice ? 1 : 0,
    ...(params.password && { password: params.password }),
  };

  const response = await loginApiInstance.post("/admin/api/login_check.php", data);

  if (!response.data.success) {
    throw new Error(response.data.message || response.data.error || "Reset code verification failed");
  }

  return {
    success: response.data.success,
    message: response.data.message,
    userId: extractUserId(response.data),
    username: usernameFromJwt(response.data.jwt) ?? response.data.username,
    firstName: response.data.firstName,
    lastName: response.data.lastName,
    routerId: response.data.router_info?.router_id,
    permissions: response.data.permissions,
    router_info: response.data.router_info,
  } as Verify2FAResponse;
}
